import { applyHtmlSpecialChars, generateRandomNumber } from "../util/";
import { CreateElement } from "./element";
import { CreateParagraph, OptionsParagraph } from "./paragraph";
import { CreateRpr, OptionsRpr } from "./rpr";

interface OptionsPageNumber extends OptionsRpr {
    /** Default value. Default as 1 for numerical and a for alphabetical */
    defaultValue?: string;
    /** Page of strings */
    stringsPageOf?: {
        // Default as "Page"
        page?: string;
        // Default as "of"
        of?: string;
    };
    /** Page number type. Default as numerical */
    type?: "numerical" | "alphabetical" | "page-of";
}

/**
 * Create page number
 */
class CreatePageNumber extends CreateElement {
    /**
     * Element options
     */
    protected options: OptionsPageNumber;

    /**
     * Element options paragraph
     */
    protected optionsParagraph: OptionsParagraph;

    /**
     * Constructor
     *
     * @param options - Options page number
     * @param optionsParagraph - Options paragraph
     */
    constructor(
        options: OptionsPageNumber,
        optionsParagraph: OptionsParagraph = {}
    ) {
        super();

        // default options
        options.stringsPageOf ??= { page: "Page", of: "of" };
        options.type ??= "numerical";
        options.defaultValue ??= options.type == "numerical" ? "1" : "a";

        this.options = options;
        this.optionsParagraph = optionsParagraph;

        // apply paragraph rPr styles
        this.options = this.mergeParagraphRprStyles(options, optionsParagraph);
    }

    /**
     * Creates content
     *
     * @returns Contents
     */
    public create(): string {
        // rPr styles
        const rPrElement = new CreateRpr(this.options);
        const rPrStyles = rPrElement.create();

        let contents = "";

        if (
            this.options.type == "numerical" ||
            this.options.type == "alphabetical"
        ) {
            const sdtPrId = generateRandomNumber(100000000, 999999999, []);
            contents =
                '<w:sdt><w:sdtPr><w:id w:val="' +
                sdtPrId +
                '"/><w:docPartObj><w:docPartGallery w:val="Page Numbers"/><w:docPartUnique/></w:docPartObj></w:sdtPr><w:sdtContent>';
            contents += "";
            let pageMergeFormat = "PAGE \\* MERGEFORMAT ";
            if (this.options.type == "alphabetical") {
                pageMergeFormat = "PAGE \\* alphabetic \\* MERGEFORMAT ";
            }
            const sdtContent =
                "<w:r>" +
                rPrStyles +
                '<w:fldChar w:fldCharType="begin"/></w:r><w:r>' +
                rPrStyles +
                '<w:instrText xml:space="preserve">' +
                pageMergeFormat +
                "</w:instrText></w:r><w:r>" +
                rPrStyles +
                '<w:fldChar w:fldCharType="separate"/></w:r><w:r>' +
                rPrStyles +
                "<w:t>" +
                this.options.defaultValue +
                "</w:t></w:r><w:r>" +
                rPrStyles +
                '<w:fldChar w:fldCharType="end"/></w:r>';

            // wrap in a paragraph
            const paragraphElement = new CreateParagraph(this.optionsParagraph);
            contents += paragraphElement.create(sdtContent);

            contents += "</w:sdtContent></w:sdt>";
        } else if (this.options.type == "page-of") {
            const sdtPrIdParent = generateRandomNumber(
                100000000,
                999999999,
                []
            );
            const sdtPrIdChild = generateRandomNumber(100000000, 999999999, [
                sdtPrIdParent,
            ]);
            contents =
                '<w:sdt><w:sdtPr><w:id w:val="' +
                sdtPrIdParent +
                '" /><w:docPartObj><w:docPartGallery w:val="Page Numbers" /><w:docPartUnique /></w:docPartObj></w:sdtPr><w:sdtContent><w:sdt><w:sdtPr><w:id w:val="' +
                sdtPrIdChild +
                '" /><w:docPartObj><w:docPartGallery w:val="Page Numbers" /><w:docPartUnique /></w:docPartObj></w:sdtPr><w:sdtContent>';
            const pageString = this.options.stringsPageOf?.page
                ? applyHtmlSpecialChars(this.options.stringsPageOf?.page)
                : "Page";
            const ofString = this.options.stringsPageOf?.of
                ? applyHtmlSpecialChars(this.options.stringsPageOf?.of)
                : "of";
            const sdtContent =
                "<w:r>" +
                rPrStyles +
                '<w:t xml:space="preserve">' +
                pageString +
                " </w:t></w:r><w:r>" +
                rPrStyles +
                '<w:fldChar w:fldCharType="begin" /></w:r><w:r>' +
                rPrStyles +
                '<w:instrText xml:space="preserve">PAGE </w:instrText></w:r><w:r>' +
                rPrStyles +
                '<w:fldChar w:fldCharType="separate" /></w:r><w:r>' +
                rPrStyles +
                "<w:t>" +
                this.options.defaultValue +
                "</w:t></w:r><w:r>" +
                rPrStyles +
                '<w:fldChar w:fldCharType="end" /></w:r><w:r>' +
                rPrStyles +
                '<w:t xml:space="preserve"> ' +
                ofString +
                " </w:t></w:r><w:r>" +
                rPrStyles +
                '<w:fldChar w:fldCharType="begin" /></w:r><w:r>' +
                rPrStyles +
                '<w:instrText xml:space="preserve">NUMPAGES </w:instrText></w:r><w:r>' +
                rPrStyles +
                '<w:fldChar w:fldCharType="separate" /></w:r><w:r>' +
                rPrStyles +
                "<w:t>" +
                this.options.defaultValue +
                "</w:t></w:r><w:r>" +
                rPrStyles +
                '<w:fldChar w:fldCharType="end" /></w:r>';

            // wrap in a paragraph
            const paragraphElement = new CreateParagraph(this.optionsParagraph);
            contents += paragraphElement.create(sdtContent);

            contents += "</w:sdtContent></w:sdt></w:sdtContent></w:sdt>";
        }

        return contents;
    }
}

export { CreatePageNumber, OptionsPageNumber };
